import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../api';
import toast from 'react-hot-toast';
import Logo from '../components/Logo';
import Turnstile, { TURNSTILE_SITE_KEY } from '../components/Turnstile';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);

  const captchaRequired = !!TURNSTILE_SITE_KEY;
  const canSubmit = !!email.trim() && (!captchaRequired || !!captchaToken) && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (captchaRequired && !captchaToken) {
      toast.error('Complete the CAPTCHA and try again.');
      return;
    }
    setLoading(true);
    try {
      await authAPI.forgotPassword(email.trim(), captchaToken || undefined);
      setSubmitted(true);
      toast.success('If an account exists, you will receive a reset link.');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to send reset email. Try again.');
      setCaptchaToken(null);
      setCaptchaReset((n) => n + 1);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-6 bg-white p-8 rounded-lg shadow-md">
          <div className="flex justify-center"><Logo size="lg" showTagline={false} /></div>
          <h2 className="text-center text-xl font-bold text-gray-900">Check your email</h2>
          <p className="text-center text-gray-600">
            If an account exists with <strong>{email}</strong>, you will receive a password reset link or sign-in instructions shortly.
          </p>
          <p className="text-center text-sm text-gray-500">
            The link expires in 1 hour. Check your spam folder if you don&apos;t see it.
          </p>
          <Link
            to="/login"
            className="block text-center text-green-600 hover:text-green-700 font-medium"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
        <div className="flex flex-col items-center">
          <Logo size="lg" showTagline={false} className="mb-4" />
          <h2 className="text-center text-2xl font-extrabold text-gray-900">
            Forgot your password?
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your email and we&apos;ll send you a link to reset it.
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-green-500 focus:border-green-500"
              placeholder="Email address"
            />
          </div>
          {captchaRequired ? (
            <div>
              <Turnstile onToken={setCaptchaToken} resetSignal={captchaReset} />
              {!captchaToken ? (
                <p className="mt-2 text-xs text-gray-500">Complete the check above to continue.</p>
              ) : null}
            </div>
          ) : null}
          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-green-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Send reset link'}
          </button>
        </form>
        <p className="text-center text-sm text-gray-600">
          <Link to="/login" className="font-medium text-green-600 hover:text-green-700">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
